import { defineStore } from 'pinia'
import { useSyncData } from '@/composables/useSyncData'
import { CustomField, CustomFieldModelType } from '@/_api/models/customField'

export const useCustomFieldsStore = defineStore('customFields', () => {
  const { $api, $grants, $features } = useNuxtApp()
  const authStore = useAuthStore()

  const accountId = computed(() => authStore.activeGuid)

  const {
    items: customFields,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    sync: _sync,
    findById,
  } = useSyncData<CustomField>({
    service: $api.customField,
    key: 'customFields',
    accountId,
    grants: { customField: ['view'] },
  })

  const sync = async (options?: { force?: boolean; change?: boolean }) => {
    if ($features && !$features.get('custom-fields')) {
      return
    }

    if ($grants && !$grants.all({ customField: ['view'] })) {
      return
    }

    await _sync(options)
  }

  const syncDate = computed(() => syncState.value.date)

  const all = computed(() => customFields.value)

  const byType = (modelType: CustomFieldModelType) => {
    return customFields.value.filter(
      (field: CustomField) => field.modelType === modelType
    ).sort((a, b) => (a?.listOrder ?? 0) - (b?.listOrder ?? 0))
  }

  const customer = computed(() => byType(CustomFieldModelType.customer))

  const reservation = computed(() => byType(CustomFieldModelType.reservation))

  const order = computed(() => byType(CustomFieldModelType.order))

  const employee = computed(() => byType(CustomFieldModelType.employee))

  const service = computed(() => byType(CustomFieldModelType.service))

  const product = computed(() => byType(CustomFieldModelType.product))

  const reservationCustomer = computed(() =>
    byType(CustomFieldModelType.reservationCustomer)
  )

  return {
    customFields,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    syncDate,
    sync,
    findById,
    all,
    byType,
    customer,
    reservation,
    order,
    employee,
    service,
    product,
    reservationCustomer,
  }
})
